'use client';

import { useState, Suspense } from 'react';
import { api, ApiClientError } from '@/lib/api-client';
import Link from 'next/link';
// D-LOW-003 fix (SPEC-044 QA AUDIT R1 — Batch 4): explicit `<Label htmlFor>` +
// `<Input id>` replaces implicit `<label><span>X</span><input/></label>`.
// The implicit pattern wraps the input inside the label element — the
// browser DOES associate them (clicking the label focuses the input),
// BUT screen readers (NVDA, JAWS, VoiceOver) sometimes miss the
// association when the label wraps multiple elements + the input has
// complex attributes. The explicit pattern (`<Label htmlFor="id">` +
// `<Input id="id">`) is the WCAG 2.1 SC 1.3.1 + SC 4.1.2 recommended
// pattern — it's unambiguous + works in every screen reader + can be
// tested with axe-core. Spec ref: SPEC-027 §3 (Accessibility), WCAG 2.1
// SC 1.3.1 (Info and Relationships) + SC 4.1.2 (Name, Role, Value).
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
// SPEC-050 Bug 1: replace native `<button>` with shadcn `<Button>` so the
// CTA inherits theme-aware tokens + the Bug 8 mobile touch-target sizing.
import { Button } from '@/components/ui/button';

/**
 * Forgot password page — SPEC-010 §3.2.3
 * User enters email, backend sends reset link if email exists.
 * Always shows success message (anti-enumeration).
 */
export default function ForgotPasswordPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-muted-foreground">Cargando…</div>}>
      <ForgotPasswordContent />
    </Suspense>
  );
}

function ForgotPasswordContent() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      // AUDIT-017 fix (FASE 3 C3): extraer mensaje de forma segura.
      if (err instanceof ApiClientError) {
        setError(err.message || 'Error al enviar el email.');
      } else if (err instanceof Error) {
        setError(err.message || 'Error al enviar el email.');
      } else if (typeof err === 'string') {
        setError(err);
      } else {
        setError('Error inesperado. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <main id="main-content" className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold tracking-tight">
            <span className="text-[#06b6d4]">Profile</span>
            <span className="text-[#8b5cf6]">ia</span>
          </h1>
        </div>

        <div className="bg-card border border-border backdrop-blur rounded-2xl p-8 shadow-2xl">
          {sent ? (
            <>
              <h2 className="text-xl font-semibold mb-4 text-foreground">Email enviado</h2>
              <p className="text-muted-foreground mb-6">
                Si el email está registrado, recibirás un enlace para restablecer tu contraseña.
                El enlace expira en 1 hora y es de un solo uso.
              </p>
              <Button asChild className="w-full bg-gradient-to-r from-[#2563eb] to-[#8b5cf6] text-white hover:opacity-90 shadow-lg">
                <Link href="/login">Volver al login</Link>
              </Button>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold mb-2 text-foreground">Restablecer contraseña</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>

              {error && (
                <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <form onSubmit={onSubmit}>
                <div className="block mb-6">
                  <Label htmlFor="forgot-email" className="block text-sm font-medium text-foreground mb-1">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#2563eb] to-[#8b5cf6] text-white hover:opacity-90 shadow-lg"
                >
                  {loading ? 'Enviando…' : 'Enviar enlace'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link href="/login" className="text-sm text-primary hover:underline">
                  Volver al login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
